import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'

interface TypographyProps extends VariantProps<typeof typographyVariants> {
  asChild?: boolean
  className?: string
  children: React.ReactNode
}

export const variants = {
  'body-01': 'text-base leading-5',
  'body-compact-01': 'leading-4',
  'body-01-m': 'leading-5 font-medium',
  'body-compact-01-m': 'leading-4 font-medium',
  'body-02': 'text-16 leading-6',
  'body-02-m': 'text-16 leading-6 font-medium',
  'body-compact-02': 'text-16 leading-[18px]',
  'body-compact-02-m': 'text-16 leading-[18px] font-medium',
  'cf-heading-01': 'text-14 leading-5 font-semibold',
  'cf-heading-compact-01': 'text-14 leading-4 font-semibold',
  'cf-heading-02': 'text-16 leading-6 font-semibold',
  'cf-heading-compact-02': 'text-16 leading-[18px] font-semibold',
  'cf-heading-03': 'text-20 leading-7 font-semibold',
  'cf-heading-compact-03': 'text-20 leading-6 font-semibold',
  'cf-label-01': 'text-11 leading-4',
  'cf-label-compact-01': 'text-11 leading-[13px]',
  'cf-label-01-M': 'text-11 leading-4 font-medium',
  'cf-label-compact-01-M': 'text-11 leading-[13px] font-medium',
  'cf-label-01-B': 'text-11 leading-4 font-bold',
  'cf-label-compact-01-B': 'text-11 leading-[13px] font-bold',
  'cf-label-02': 'text-12 leading-4',
  'cf-label-02-compact': 'text-12 leading-[14px]',
  'cf-label-02-M': 'text-12 leading-4 font-medium',
  'cf-label-compact-02-M': 'text-12 leading-[14px] font-medium',
  'cf-label-02-B': 'text-12 leading-4 font-bold',
  'cf-label-02-compact-B': 'text-12 leading-[14px] font-bold',
  'cf-title-01': 'text-14 leading-5 font-bold',
  'cf-title-compact-01': 'text-14 leading-[18px] font-bold',
  'cf-title-02': 'text-16 leading-6 font-bold',
  'cf-title-compact-02': 'text-16 leading-[18px] font-bold',
  'cf-title-03': 'text-18 leading-[26px] font-bold',
  'cf-title-compact-03': 'text-18 leading-[22px] font-bold',
  'cf-title-04': 'text-20 leading-[28px] font-bold',
  'cf-title-05': 'text-24 leading-[30px] font-bold',
  'cf-title-06': 'text-[28px] leading-[36px] font-bold',
  'cf-title-07': 'text-[32px] leading-10 font-bold',
  'cf-title-08': 'md:text-[40px] text-[32px] leading-[46px] font-bold',
  'cf-title-09': 'text-[48px] leading-[54px] font-bold',
  'cf-title-10': 'text-[56px] leading-[62px] font-bold',
  'cf-title-11': 'text-[64px] leading-[70px] font-bold',
  'cf-title-02-fc': 'text-16 leading-5 font-bold uppercase',
  'cf-title-compact-02-fc': 'text-16 leading-[18px] font-bold uppercase',
} as const

export const typographyVariants = cva('font-poppins text-cf-sf-gray-1100', {
  variants: {
    variant: variants,
  },
  defaultVariants: {
    variant: 'body-01',
  },
})

export const Typography = ({
  asChild,
  variant,
  className,
  ...props
}: TypographyProps & React.HTMLAttributes<HTMLParagraphElement>) => {
  const Component = asChild ? Slot : 'p'

  const typographyClasses = tw(typographyVariants({ variant, className }))

  return <Component {...props} className={typographyClasses} />
}
